import React, {createContext, useContext, useMemo} from 'react';

import {FlowchartStaticEdgeData, TransitionSource} from '../../processFlowchartConfigEditorConstants';
import {StaticFlowchartNodeData, TaskSchemaDomainData} from '../../processFlowchartConfigEditorNodeTypes';
import {
  getTransitionSourceForEdgeWithDefault,
  getTransitionSourceForNodePairWithDefault,
} from './transitionLookupUtils';
import {Transition} from './TransitionTypes';

export interface TransitionsContextValue {
  /** All transitions keyed by transition ID */
  transitionsById: Record<string, Transition>;
  /** Index mapping PTDV pairs to transition IDs for quick lookup */
  ptdvPairToTransitionIdIndex: Record<string, string>;
}

const TransitionsContext = createContext<TransitionsContextValue | null>(null);

export interface TransitionsContextProviderProps {
  children: React.ReactNode;
  value: TransitionsContextValue;
}

export function TransitionsContextProvider(props: TransitionsContextProviderProps): JSX.Element {
  return <TransitionsContext.Provider value={props.value}>{props.children}</TransitionsContext.Provider>;
}

export function useTransitionsContext(): TransitionsContextValue {
  const context = useContext(TransitionsContext);
  if (context == null) {
    throw new Error('useTransitionsContext must be used within a TransitionsContextProvider');
  }
  return context;
}

export function useTransitionSourceForNodePair(
  sourceNodeId: string,
  targetNodeId: string,
  nodes: Array<TaskSchemaDomainData>
): TransitionSource {
  const {transitionsById, ptdvPairToTransitionIdIndex} = useTransitionsContext();

  return useMemo(
    () =>
      getTransitionSourceForNodePairWithDefault({
        sourceNodeId,
        targetNodeId,
        nodes,
        transitionsById,
        ptdvPairToTransitionIdIndex,
        nodesForDefault: nodes,
      }),
    [sourceNodeId, targetNodeId, nodes, transitionsById, ptdvPairToTransitionIdIndex]
  );
}

export function useTransitionSourceForEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>
): TransitionSource {
  const {transitionsById, ptdvPairToTransitionIdIndex} = useTransitionsContext();

  return useMemo(
    () =>
      getTransitionSourceForEdgeWithDefault({
        edge,
        nodes,
        transitionsById,
        ptdvPairToTransitionIdIndex,
        nodesForDefault: nodes,
      }),
    [edge, nodes, transitionsById, ptdvPairToTransitionIdIndex]
  );
}
